import React, { Component } from 'react';
import PropTypes from 'prop-types';
import getCaretCoordinates from 'textarea-caret';

import MarkdownMentions from '../markdown_with_mentions';
import MarkdownInput from './MarkdownInput';
import MentionsPicker from './MentionsPicker';

// Key codes of keydown and keyup events, inspired by https://git.io/vQwAK
const KEY = {
  TAB: 9,
  RETURN: 13,
  ESC: 27,
  SPACE: 32,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  ALT: 18,
};

export default class MentionsInput extends Component {
  static propTypes = {
    value: PropTypes.string,
    clear: PropTypes.bool,
    mentionPattern: PropTypes.string,
    mentionSymbol: PropTypes.shape({
      user: PropTypes.string.isRequired,
      company: PropTypes.string.isRequired,
    }),
    onChange: PropTypes.func,
    innerRef: PropTypes.object,
    apiUserUrl: PropTypes.string.isRequired,
    apiCompanyUrl: PropTypes.string.isRequired,
    renderMarkdown: PropTypes.func,
  }

  static defaultProps = {
    value: '',
    clear: false,
    mentionPattern: '\\da-zA-Z-',
    mentionSymbol: { user: '@', company: '$' },
    renderMarkdown: value => MarkdownMentions.render(value),
    onChange() {},
    innerRef: null,
  }

  constructor(props) {
    super(props);
    this.textarea = this.props.innerRef || React.createRef();
    this.state = {
      showPicker: false,
      pickerOptions: [],
      value: (this.props.value || ''),
    };
  }

  handleChange = (value) => {
    this.setState({ value });
    this.props.onChange(value);
  }

  // Get the mention word that is around the cursor position
  getCurrentMention() {
    const symbols = `${this.props.mentionSymbol.user}${this.props.mentionSymbol.company}`;
    const afterSlugPattern = new RegExp(`[^${this.props.mentionPattern}]`);
    const autocompletePattern = new RegExp(`(^|\\s)([${symbols}])([^${symbols}]+)$`);

    // In case our cursor is not at the end of the mention, pick the rest of
    // the word by looking for the next character that is not valid for slugs
    const cursorPos = this.textarea.current.selectionEnd;
    const nextSpacePos = cursorPos + this.textarea.current.value.substr(cursorPos).search(afterSlugPattern);

    // In case our search returned -1 or we reached the end of string, use the end of string
    const endWordPos = nextSpacePos < cursorPos ? this.textarea.current.value.length : nextSpacePos;

    const match = this.textarea.current.value.substr(0, endWordPos).match(autocompletePattern);

    return match ? {
      symbol: match[2],
      text: match[3],
      index: match.index + match[1].length + match[2].length, // Index is beggining of 3rd match
    } : {};
  }

  handleKeyUp = (e) => {
    const mention = this.getCurrentMention();
    const pressedArrowKey = [KEY.DOWN, KEY.LEFT, KEY.RIGHT, KEY.UP].indexOf(e.keyCode) !== -1;
    const pressedNavigationKey = e.keyCode === KEY.ESC || e.keyCode === KEY.TAB ||
      e.keyCode === KEY.RETURN || e.keyCode === KEY.ALT;

    // Don't do anything if the user is navigating with the arrows
    if (!this.state.showPicker && pressedArrowKey) return;

    if (pressedNavigationKey || (this.state.showPicker && !mention.text)) {
      this.clearAutocomplete();
    } else if (mention.text) {
      this.setState({
        showPicker: true,
        mentionText: mention.text,
        mentionSymbol: mention.symbol,
      });
    }
  }

  handleKeyDown = (e) => {
    if (!this.state.showPicker) return;

    switch (e.keyCode) {
      case KEY.UP:
        e.preventDefault();
        this.shiftSelectedIndex(-1);
        break;
      case KEY.DOWN:
        e.preventDefault();
        this.shiftSelectedIndex(+1);
        break;
      case KEY.TAB:
      case KEY.RETURN: {
        e.preventDefault();
        const option = this.state.pickerOptions[this.state.pickerSelectedIndex];
        if (option) this.insertMention(option.slug);
        break;
      }
      default:
    }
  }

  handleMentionMouseEnter = (pickerSelectedIndex) => {
    this.setState({ pickerSelectedIndex });
  }

  handleMentionClick = (option) => {
    this.insertMention(option.slug);
  }

  handleClick = () => {
    this.clearAutocomplete();
  }

  handleClickOutside = () => {
    this.clearAutocomplete();
  }

  insertMention(slug) {
    const mention = this.getCurrentMention();
    const text = `${slug} `;
    let value = this.state.value;
    value = value.substr(0, mention.index) + text + value.substr(mention.index + mention.text.length);

    this.setState({ value, showPicker: false }, () => {
      this.textarea.current.setSelectionRange(mention.index + text.length, mention.index + text.length);
      this.textarea.current.focus();
    });
  }

  shiftSelectedIndex = (delta) => {
    const count = this.state.pickerOptions.length;
    this.setState({
      pickerSelectedIndex: (count + this.state.pickerSelectedIndex + delta) % count,
    });
  };

  // Map the API response to the format used by MentionPicker
  responseToProps(json) {
    return json.map((item) => ({
      key: item.slug,
      title: item.name,
      subtitle: `${item.object_type === 'company' ?
        this.props.mentionSymbol.company : this.props.mentionSymbol.user}${item.slug}`,
      type: item.object_type,
      avatar: item.avatar_url,
      slug: item.slug, // Used in the click handler
    }));
  }

  clearAutocomplete() {
    this.setState({
      showPicker: false,
      pickerOptions: [],
      pickerSelectedIndex: 0,
    });
  }

  performAutocomplete(symbol, text) {
    if (this.request) this.request.abort();
    if (this.timeout) clearTimeout(this.timeout);

    const apiUrl = symbol === this.props.mentionSymbol.user ? this.props.apiUserUrl : this.props.apiCompanyUrl;
    const params = { query: text, limit: 30, search_slug: true };

    this.timeout = setTimeout(() => {
      this.setState({ pickerLoading: true });
    }, 300);

    this.request = $.get(apiUrl, params, (response) => {
      if (this.timeout) clearTimeout(this.timeout);

      this.setState({
        pickerLoading: false,
        pickerOptions: this.responseToProps(response),
        showPicker: response.length > 0,
      });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.mentionText !== this.state.mentionText) {
      this.performAutocomplete(this.state.mentionSymbol, this.state.mentionText);
    }
    if (prevState.value !== this.props.value && this.props.clear) {
      this.setState({ value: this.props.value });
    }
  }

  render() {
    const { apiUserUrl, apiCompanyUrl, mentionPattern, mentionSymbol, clear, ...textareaProps } = this.props;
    let mentionsPickerPosition = {};

    if (this.state.showPicker) {
      const caret = getCaretCoordinates(this.textarea.current, this.textarea.current.selectionEnd);
      mentionsPickerPosition = { position: 'absolute', top: caret.top + 20, left: caret.left };
    }

    return (
      <MarkdownInput {...textareaProps}
        innerRef={this.textarea}
        value={this.state.value}
        onChange={this.handleChange}
        onKeyUp={this.handleKeyUp}
        onKeyDown={this.handleKeyDown}
        onClick={this.handleClick}
        onClickOutside={this.handleClickOutside}
      >
        { this.state.showPicker &&
          <MentionsPicker
            style={mentionsPickerPosition}
            options={this.state.pickerOptions}
            selected={this.state.pickerSelectedIndex}
            onClick={this.handleMentionClick}
            onMouseEnter={this.handleMentionMouseEnter}
            searchWords={this.state.mentionText && [this.state.mentionText]}
            loading={this.state.pickerLoading}
          />
        }
      </MarkdownInput>
    );
  }
}
